import { FormControl, FormHelperText } from "@mui/material";
import { useEffect, useMemo, useState, type FC } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import {
  hubSettingsEntriesToMap,
  isInnerItemEditingAtom,
  Log,
  parseHubSettingsEntries,
  type ICustomField,
  type TStationHubMap,
} from "@ryde/shared";

import { GroupTitleTemplate } from "../group_title.template";
import { StationHubItemAdd } from "./station-hub-item.add";
import { StationHubRow } from "./station-hub-row";
import type { IStationHubItem } from "./table-station-hub.models";
import { TableStationHubStyled } from "./table-station-hub.styled";

export const TableStationHubTemplate: FC<ICustomField> = ({
  name,
  formLabel,
  formAttributes,
}) => {
  const { control, setValue, watch } = useFormContext();
  const [editingCount, setEditingCount] = useState(0);
  const setIsInnerItemEditing = useSetRecoilState(isInnerItemEditingAtom);
  const currentStationId = watch("id") as number | undefined;
  const watchedValue = useWatch({
    control,
    name,
    defaultValue: {},
  }) as TStationHubMap;

  const stationsHubList = useMemo<IStationHubItem[]>(() => {
    return parseHubSettingsEntries(watchedValue).map(entry => ({
      destinationStationId: entry.pairedStationId,
      hubId: entry.hubId,
      direction: entry.direction,
    }));
  }, [watchedValue]);

  const updateField = useEvent((newValue: TStationHubMap) => {
    setValue(name, newValue, {
      shouldDirty: true,
      shouldValidate: true,
    });
  });

  const handleRemove = useEvent((item: IStationHubItem) => {
    Log.debug("Remove station hub handler fired:", item);

    const nextList = stationsHubList.filter(
      listItem =>
        listItem.destinationStationId !== item.destinationStationId ||
        listItem.direction !== item.direction,
    );
    updateField(
      hubSettingsEntriesToMap(
        nextList.map(listItem => ({
          pairedStationId: listItem.destinationStationId,
          hubId: listItem.hubId,
          direction: listItem.direction,
        })),
      ),
    );
  });

  const handleAdd = useEvent((item: IStationHubItem) => {
    Log.debug("Add station hub handler fired:", item);

    updateField(
      hubSettingsEntriesToMap(
        [...stationsHubList, item].map(listItem => ({
          pairedStationId: listItem.destinationStationId,
          hubId: listItem.hubId,
          direction: listItem.direction,
        })),
      ),
    );
  });

  const handleUpdated = useEvent(
    (prevValue: IStationHubItem, newValue: IStationHubItem) => {
      Log.debug("Update station hub handler fired:", prevValue, newValue);

      const nextList = stationsHubList
        .filter(
          listItem =>
            listItem.destinationStationId !== prevValue.destinationStationId ||
            listItem.direction !== prevValue.direction,
        )
        .concat(newValue);

      updateField(
        hubSettingsEntriesToMap(
          nextList.map(listItem => ({
            pairedStationId: listItem.destinationStationId,
            hubId: listItem.hubId,
            direction: listItem.direction,
          })),
        ),
      );
    },
  );

  const { disabled } = formAttributes || {};

  const handleEditStart = useEvent(() => {
    setEditingCount(prev => prev + 1);
  });

  const existingItems = stationsHubList;

  const handleEditEnd = useEvent(() => {
    setEditingCount(prev => Math.max(0, prev - 1));
  });

  useEffect(() => {
    setIsInnerItemEditing(editingCount > 0);
  }, [editingCount, setIsInnerItemEditing]);

  useEffect(() => {
    return () => {
      setIsInnerItemEditing(false);
    };
  }, [setIsInnerItemEditing]);

  return (
    <TableStationHubStyled>
      <Controller
        name={name}
        control={control}
        render={({ fieldState: { error } }) => {
          return (
            <FormControl
              error={!!error}
              data-e2e-template="table-station-hub-template"
            >
              <GroupTitleTemplate
                formLabel={formLabel}
                name={name}
                formAttributes={formAttributes}
              />
              <div
                className="stationHub__container"
                data-inner-editing={editingCount > 0 ? "true" : undefined}
              >
                {stationsHubList?.map((field, index) => {
                  return (
                    <StationHubRow
                      key={`${index}_${field.destinationStationId}_${field.hubId}_${field.direction}`}
                      item={field}
                      existingItems={existingItems.filter(
                        listItem =>
                          listItem.destinationStationId !==
                            field.destinationStationId ||
                          listItem.direction !== field.direction,
                      )}
                      currentStationId={currentStationId}
                      disabledEdit={disabled}
                      disabledRemove={disabled}
                      onRemove={handleRemove}
                      onUpdated={handleUpdated}
                      onEditStart={handleEditStart}
                      onEditEnd={handleEditEnd}
                    />
                  );
                })}

                {!disabled && (
                  <StationHubItemAdd
                    existingItems={existingItems}
                    currentStationId={currentStationId}
                    onAdded={handleAdd}
                    onEditStart={handleEditStart}
                    onEditEnd={handleEditEnd}
                  />
                )}
              </div>
              {error && <FormHelperText>{error.message}</FormHelperText>}
            </FormControl>
          );
        }}
      />
    </TableStationHubStyled>
  );
};
